import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Clock, CheckCircle, XCircle, Plus } from 'lucide-react';
import { useAuth } from '../../../auth/hooks/auth';
import { authorAuthService, AuthorDashboardStats, SubmissionItem } from '../services/authorAuth';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [stats, setStats] = useState<AuthorDashboardStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [recentSubmissions, setRecentSubmissions] = useState<SubmissionItem[]>([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(true);
  const [activeCycleId, setActiveCycleId] = useState<number | null>(null);

  useEffect(() => {
    fetchActiveCycle();
    fetchDashboardStats();
  }, []);

  useEffect(() => {
    if (activeCycleId !== null) {
      fetchRecentSubmissions();
    }
  }, [activeCycleId]);

  const fetchActiveCycle = async () => {
    try {
      const response = await authorAuthService.getRunningCycle();
      if (response.isSuccess && response.data) {
        setActiveCycleId(response.data.id);
      } else {
        // If fetch fails, load submissions with 0
        fetchRecentSubmissions();
      }
    } catch (error) {
      fetchRecentSubmissions();
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const response = await authorAuthService.getAuthorDashboardStatistics();
      if (response.isSuccess && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      } finally {
      setStatsLoading(false);
    }
  };

  const fetchRecentSubmissions = async () => {
    if (!user?.id) return;
    try {
      const response = await authorAuthService.getSubmissions({
        pageNumber: 1,
        pageSize: 3,
        sortBy: '',
        sortDescending: true,
        search: '',
        categoryId: 0,
        cycleId: activeCycleId || 0,
        authorId: user.id,
        submittedFrom: null,
        submittedTo: null,
        applicationType: 0
      });
      if (response.isSuccess && response.data) {
        setRecentSubmissions(response.data.data);
      }
    } catch (error) {
      } finally {
      setSubmissionsLoading(false);
    }
  };

  const getStatusBadge = (statusName?: string) => {
    const name = (statusName || '').toLowerCase();
    if (name.includes('approved') || name.includes('passed')) {
      return <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Approved</span>;
    } else if (name.includes('review')) {
      return <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Under Review</span>;
    } else if (name.includes('reject') || name.includes('failed')) {
      return <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Rejected</span>;
    } else if (name.includes('draft')) {
      return <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">Draft</span>;
    }
    return <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">{statusName || 'Submitted'}</span>;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  };

  // Build subtitle for total submissions card
  const getSubmissionBreakdown = () => {
    if (!stats?.submissionsByApplicationType) return '';
    const entries = Object.entries(stats.submissionsByApplicationType);
    if (entries.length === 0) return '';
    return entries.map(([type, count]) => `${type}: ${count}`).join(', ');
  };

  return (
    <>
      <div className="mb-8">
        <h1 className="text-3xl font-normal text-gray-900 mb-2">Welcome back, {user?.name || 'User'}</h1>
        <p className="text-gray-500">Here's an overview of your manuscript submissions</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statsLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm animate-pulse">
              <div className="h-3 bg-gray-200 rounded w-24 mb-3"></div>
              <div className="h-8 bg-gray-200 rounded w-12 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-32"></div>
            </div>
          ))
        ) : (
          <>
            <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm flex items-start justify-between">
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide font-medium mb-2">Total Submissions</p>
                <h3 className="text-3xl font-bold text-gray-900 mb-1">{stats?.totalSubmissions ?? 0}</h3>
                <p className="text-gray-400 text-xs">{getSubmissionBreakdown() || 'No submissions yet'}</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg text-blue-600">
                <FileText size={24} />
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm flex items-start justify-between">
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide font-medium mb-2">Under Review</p>
                <h3 className="text-3xl font-bold text-gray-900 mb-1">{stats?.underReviewSubmissions ?? 0}</h3>
                <p className="text-gray-400 text-xs">Awaiting review</p>
              </div>
              <div className="bg-yellow-50 p-3 rounded-lg text-yellow-600">
                <Clock size={24} />
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm flex items-start justify-between">
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide font-medium mb-2">Approved</p>
                <h3 className="text-3xl font-bold text-gray-900 mb-1">{stats?.approvedSubmissions ?? 0}</h3>
                <p className="text-gray-400 text-xs">Successfully approved</p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg text-green-600">
                <CheckCircle size={24} />
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm flex items-start justify-between">
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide font-medium mb-2">Rejected</p>
                <h3 className="text-3xl font-bold text-gray-900 mb-1">{stats?.rejectedSubmissions ?? 0}</h3>
                <p className="text-gray-400 text-xs">Not approved</p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg text-red-600">
                <XCircle size={24} />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm mb-8">
        <h4 className="font-semibold text-gray-900 mb-1">Quick Actions</h4>
        <p className="text-gray-500 text-sm mb-4">Get started with your next manuscript</p>
        <div className="flex gap-4">
          {user?.roleId === 13 ? (
            <button
              onClick={() => navigate("/author/application-for-journal-support")}
              className="flex items-center gap-2 bg-[#00A63E] hover:bg-[#008c34] text-white px-5 py-2.5 rounded-lg font-medium transition-colors"
            >
              <Plus size={18} /> New Journal Application
            </button>
          ) : (
            <button
              onClick={() => navigate("/author/create-manuscript")}
              className="flex items-center gap-2 bg-[#00A63E] hover:bg-[#008c34] text-white px-5 py-2.5 rounded-lg font-medium transition-colors"
            >
              <Plus size={18} /> Create New Manuscript
            </button>
          )}
          <button
            onClick={() => navigate("/author/submissions")}
            className="flex items-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-5 py-2.5 rounded-lg font-medium transition-colors"
          >
            <FileText size={18} /> View All Submissions
          </button>
        </div>
      </div>

      {/* Recent Submissions */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
          <div>
            <h4 className="font-semibold text-gray-900">Recent Submissions</h4>
            <p className="text-gray-500 text-sm">Track your latest manuscript submissions</p>
          </div>
          <button onClick={() => navigate('/author/submissions')} className="text-sm font-medium text-gray-900 hover:text-[#00A63E]">
            View all
          </button>
        </div>

        <div className="divide-y divide-gray-50">
          {submissionsLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="p-4 animate-pulse flex items-center gap-4">
                <div className="h-10 w-10 rounded-lg bg-gray-200"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-48 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-24"></div>
                </div>
                <div className="h-6 bg-gray-200 rounded w-20"></div>
              </div>
            ))
          ) : recentSubmissions.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <FileText size={32} className="mx-auto mb-2 opacity-50" />
              <p>No submissions yet.</p>
            </div>
          ) : (
            recentSubmissions.map((submission) => (
              <div key={submission.id} className="p-4 hover:bg-gray-50 transition-colors flex items-center gap-4">
                <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500">
                  <FileText size={20} />
                </div>
                <div className="flex-1">
                  <h5 className="font-medium text-gray-900 text-sm mb-0.5">{submission.title}</h5>
                  <p className="text-gray-400 text-xs">ID: {submission.submissionNumber}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400 mb-1">{submission.status}</p>
                  <p className="text-sm font-medium text-gray-900">{formatDate(submission.submittedAt || submission.dateSubmitted)}</p>
                </div>
                <div className="pl-4">
                  {getStatusBadge(submission.status)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default Dashboard;